// The queue behind the bulk image drop.
//
// ─── THE CONSTRAINT THAT SHAPES THIS WHOLE SCREEN ───────────────────────────
//
// The assistant NEVER SEES THE IMAGE. It reads the description and nothing
// else, which is what the panel says in its own first line. So an image with no
// description is not "an image missing a nice-to-have field" — it is an image
// the assistant can never propose, sitting in the brain forever, invisible.
//
// ⚠️ BULK UPLOAD MULTIPLIES THAT BY TEN. One-at-a-time, forgetting a
// description costs one dead entry; dropping twelve files and pressing upload
// costs twelve. So the queue tracks describedness per file, surfaces it before
// the upload rather than after, and refuses to call a run "done" quietly when
// half of it is unusable.
//
// It does NOT block on it. The admin may genuinely want the picture in first
// and the words later, and a modal demanding twelve descriptions before
// anything uploads is how people stop using a feature.
//
// Guard: src/pages/admin/assistant/imageQueue.test.ts

export type QueueStatus = 'ready' | 'uploading' | 'done' | 'error';

export interface QueuedImage {
  /** Stable across re-renders and reorders — never the array index. */
  id: string;
  file: File;
  /** Object URL for the thumbnail; revoked when the item leaves the queue. */
  previewUrl: string;
  title: string;
  description: string;
  status: QueueStatus;
  error?: string;
}

/** What the bucket accepts. Offering anything else lets the upload fail at the
 *  very last step with a storage error the admin cannot act on. */
export const ACCEPTED_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
  // ⚠️ VIDEO, SINCE 2026-09-19. The owner has one album video and wants a
  // Relief-3D one; the relay posts them with `send-video` (whatsapp-relay/src/
  // outbox.js `sendMethodFor`), so they arrive playable rather than as a grey
  // file. The bucket accepts these two types and nothing else moving.
  'video/mp4', 'video/quicktime',
] as const;

/** WhatsApp refuses a video over 16 MB — and a customer on 3G abandons one
 *  long before that. Caught here so the admin is told before the upload, not
 *  by a send that silently never arrives. */
export const MAX_VIDEO_BYTES = 16 * 1024 * 1024;

/** Supabase storage refuses larger objects; catching it here turns a late,
 *  cryptic failure into an immediate, explainable one. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

export interface RejectedFile { name: string; reason: string }

/**
 * Split a dropped batch into what can be queued and what cannot.
 *
 * ⚠️ REJECTIONS ARE RETURNED, NOT SWALLOWED. Dropping twelve files and seeing
 * ten appear is the kind of silence that reads as a bug — the two that were a
 * PDF and a 40 MB TIFF have to say so themselves.
 */
export function partitionDropped(
  files: File[],
  existingCount = 0,
  maxTotal = 30,
): { accepted: File[]; rejected: RejectedFile[] } {
  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];
  for (const f of files) {
    if (existingCount + accepted.length >= maxTotal) {
      rejected.push({ name: f.name, reason: `limite de ${maxTotal} images par lot` });
      continue;
    }
    const type = String(f.type || '').toLowerCase();
    // Some browsers report an empty type for .heic — fall back to the extension
    // rather than rejecting the format iPhones actually produce.
    const okType = (ACCEPTED_TYPES as readonly string[]).includes(type)
      || (!type && /\.(jpe?g|png|webp|heic|heif|mp4|mov)$/i.test(f.name));
    if (!okType) { rejected.push({ name: f.name, reason: 'format non accepté' }); continue; }
    // A video that WhatsApp will refuse is rejected here, where it can be said
    // in words, rather than by a send that reports success and delivers nothing.
    const isVideo = type.startsWith('video/') || (!type && /\.(mp4|mov)$/i.test(f.name));
    if (isVideo && f.size > MAX_VIDEO_BYTES) {
      rejected.push({ name: f.name, reason: `vidéo trop lourde (${Math.round(f.size / 1024 / 1024)} Mo) — WhatsApp refuse au-delà de 16 Mo` });
      continue;
    }
    if (f.size > MAX_FILE_BYTES) {
      rejected.push({ name: f.name, reason: `trop lourde (${(f.size / 1e6).toFixed(1)} Mo, max 25 Mo)` });
      continue;
    }
    accepted.push(f);
  }
  return { accepted, rejected };
}

/**
 * A filename turned into a human title.
 *
 * ⚠️ IT IS A STARTING POINT, NOT AN ANSWER. "IMG_2029" tells the assistant
 * nothing, and neither does a UUID — but a pre-filled field the admin edits
 * beats an empty one they must invent from scratch, and it is why the upload
 * button was never gated on the title.
 */
export function titleFromFilename(name: string): string {
  return String(name ?? '')
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

/** A filename that carries no meaning — a camera roll id or an export uuid.
 *  Worth flagging alongside a missing description: both leave the assistant
 *  with nothing to match on. */
export function isMeaninglessTitle(title: string): boolean {
  const t = String(title ?? '').trim();
  if (!t) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(t)) return true;
  // IMG 2029, DSC00123, PXL 20260824, Screenshot 2026-04-11 …
  if (/^(img|dsc|pxl|photo|image|screenshot|capture)[\s_-]*[\d\s_-]*$/i.test(t)) return true;
  return false;
}

/**
 * Can the assistant ever use this entry?
 *
 * The single question this screen exists to answer, and the reason a
 * description field sits next to every thumbnail instead of one shared box.
 */
export function isUsableByAssistant(item: Pick<QueuedImage, 'description'>): boolean {
  return String(item.description ?? '').trim().length >= 10;
}

export interface QueueSummary {
  total: number;
  ready: number;
  uploading: number;
  done: number;
  failed: number;
  /** Queued items the assistant would never be able to propose. */
  undescribed: number;
  /** True while anything is still in flight. */
  busy: boolean;
}

export function summarise(items: QueuedImage[]): QueueSummary {
  const s: QueueSummary = {
    total: items.length, ready: 0, uploading: 0, done: 0, failed: 0, undescribed: 0, busy: false,
  };
  for (const i of items) {
    if (i.status === 'ready') s.ready += 1;
    if (i.status === 'uploading') s.uploading += 1;
    if (i.status === 'done') s.done += 1;
    if (i.status === 'error') s.failed += 1;
    if (i.status !== 'done' && !isUsableByAssistant(i)) s.undescribed += 1;
  }
  s.busy = s.uploading > 0;
  return s;
}

/**
 * The sentence under the upload button.
 *
 * ⚠️ IT NEVER SAYS "TERMINÉ" WHILE SOMETHING FAILED. A run that uploaded nine
 * of twelve and reported success is the same lie as the cleanup that deleted
 * 1000 of 6333 and called it done — the number that matters is the one that did
 * NOT happen.
 */
export function queueMessage(s: QueueSummary): string {
  if (s.busy) return `Envoi en cours… ${s.done}/${s.total}`;
  if (s.failed > 0 && s.done > 0) {
    return `${s.done} envoyée${s.done > 1 ? 's' : ''}, ${s.failed} échouée${s.failed > 1 ? 's' : ''} — réessayez celles en rouge.`;
  }
  if (s.failed > 0) return `${s.failed} échec${s.failed > 1 ? 's' : ''} — réessayez.`;
  if (s.total === 0) return '';
  if (s.done === s.total) return `${s.done} image${s.done > 1 ? 's' : ''} ajoutée${s.done > 1 ? 's' : ''} au cerveau.`;
  if (s.undescribed > 0) {
    return `${s.undescribed} image${s.undescribed > 1 ? 's' : ''} sans description : l’assistant ne pourra pas ${s.undescribed > 1 ? 'les' : 'la'} proposer.`;
  }
  return `${s.ready} prête${s.ready > 1 ? 's' : ''} à envoyer.`;
}
